import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  input,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import _ from 'lodash';
import {
  ReplaySubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  fromEvent,
  skip,
  takeUntil,
} from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { delayFor } from 'app/core/utilities/async-utils';
import { SnackBarComponent, SnackBarData, SnackBarType } from 'app/shared/snackbar/snackbar.component';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import BadgeQuestions from 'entity/BadgeQuestions';
import Social from 'entity/Social';
import User from 'entity/User';
import { BadgeDataValue, BaseUser } from 'messages/websocket.messages';

import { BadgingService } from '../services/badging/badging.service';
import { ChatService } from '../services/chat.service';
import { FAVORITE_INITIAL_STATE, FavoriteResponse, FavoriteService } from '../services/favorite.service';
import { PresentationService } from '../services/presentation/presentation.service';
import { QAService } from '../services/qa.service';
import { SceneService } from '../services/scene.service';
import { SideSectionService } from '../services/side-section.service';
import { TeleportService } from '../services/teleport.service';
import { Player } from '../social-pixi-canvas/player';
import { socialAppActions, socialAppSelectors } from '../state/app';

const MAIN_QUESTIONS = ['Name', 'First Name', 'Last Name', 'Title', 'Company', 'LinkedIn', 'About Me'];

const MAX_ABOUT_ME_HEIGHT = 90;

export interface PreviewUserInfo {
  userName?: string;
  companyName?: string;
  userTitle?: string;
  aboutMe?: string;
  linkedIn?: string;
}
@Component({
  selector: 'app-badging-info',
  templateUrl: './badging-info.component.html',
  styleUrls: ['./badging-info.component.scss'],
  standalone: false,
})
export class BadgingInfoComponent extends Loggable implements OnDestroy, OnInit, AfterViewInit {
  @Input() public showRoomActionIcons = false;

  // Inputs used to handle preview badge
  @Input() public isPreviewCard?: boolean;
  @Input() public customStyle?: boolean;
  @Input() public previewBadgeInfo?: PreviewUserInfo;
  @Input() public user?: User;

  public isOnboardingExperience = input<boolean>(false);

  //Inputs to handle summary badge
  @Input() public isSummaryView?: boolean;
  @Input() public summaryTimeline?: boolean;
  @Input() public summaryTimelineBadge?: BadgeQuestions[];
  @Output() public summaryBadgeClosed = new EventEmitter<boolean>();

  @ViewChild('aboutMeSection', { static: false }) private aboutMeSection?: ElementRef<HTMLDivElement>;

  public isChatOpen = false;

  private destroyed$ = new ReplaySubject<boolean>(1);
  private userId?: string;
  private social?: Social;
  private baseUser?: BaseUser;
  private connectedRoomSet?: Social[];
  private roomName?: string;
  private player?: Player;
  private mainPlayer?: Player;
  private mainPlayerId?: string;
  private isUserPresentingToRoom = false;
  private isUserPresentingToGroup = false;
  private badgeCardWasOpen = false;
  private featureChipMaxLength = 43;
  private visualIndicatorQuestion?: string;

  protected blockActionMessage = '';
  protected blockActionButtons = false;
  protected isOnPresentation = false;
  protected userName = '';
  protected badgeSubtitle = '';
  protected badgeLinkedIn?: string;
  protected badgeAboutMe?: string;
  protected badgeQuestions?: BadgeDataValue[];
  protected dragPosition = { x: -170, y: -135 };
  protected findEngaged = false;
  protected teleportEngaged = false;
  protected chatEngaged = false;
  protected defaultAboutMeMessage = `No information has been provided by this person.`;
  protected showBtn = false;
  protected userOpenOwnBadge = false;
  protected isHost = false;
  protected isOnViewport = false;
  protected newVisualIndicators = false;
  protected ringColor?: string;

  protected favoriteResponse: FavoriteResponse = FAVORITE_INITIAL_STATE;

  protected hoveringChild = false;

  public constructor(
    public sideSectionService: SideSectionService,
    private chatService: ChatService,
    private badgingService: BadgingService,
    private qaService: QAService,
    private store: Store,
    private sceneService: SceneService,
    private teleportService: TeleportService,
    private favoriteService: FavoriteService,
    private elementRef: ElementRef,
    private _snackBar: MatSnackBar,
    private presentationService: PresentationService,
  ) {
    super();

    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((social) => (this.social = social));

    this.store
      .select(coreSelectors.getUserId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((mainPlayerId) => (this.mainPlayerId = mainPlayerId));

    this.store
      .select(socialAppSelectors.isHost)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isHost) => (this.isHost = isHost));

    this.store
      .select(socialAppSelectors.getVisualIndicatorInfo)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((visualIndicatorInfo) => {
        if (visualIndicatorInfo.indicatorLocation) {
          this.visualIndicatorQuestion = visualIndicatorInfo.indicatorLocation;
        }
      });

    fromEvent(document, 'keydown.escape')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        // If is preview card, should not have close behavior
        if (!this.isPreviewCard && this.badgeCardWasOpen) {
          this.closeBadge();
        }
      });

    fromEvent(document, 'click')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event) => {
        // If is preview card, should not have close behavior
        if (!this.isPreviewCard) {
          event.preventDefault();
          event.stopPropagation();
          const clickedInside = this.elementRef.nativeElement.contains(event.target);
          if (!clickedInside && this.badgeCardWasOpen) {
            this.closeBadge();
          }
        }
      });
  }

  public async ngAfterViewInit(): Promise<void> {
    this.store
      .select(socialAppSelectors.isChatEnabled)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((enabled) => (this.isChatOpen = enabled));

    /*
      Flag to check is the badge was open,
      so we could implement the behavior of
      closing if click outside the badge card
    */
    await delayFor(1);
    this.badgeCardWasOpen = true;
  }

  public ngOnInit(): void {
    if (this.isSummaryView || this.summaryTimeline) {
      if (this.summaryTimelineBadge) {
        this.showRoomActionIcons = false;
        this.badgeQuestions = this.summaryTimelineBadge.map((item) => {
          return { propertyName: item.property ?? '', value: item.value ?? '' };
        });
      }
      this.updateFixedValues();
      return;
    }

    // If is preview card, the treatment below is not necessary
    if (this.isPreviewCard) {
      this.badgingService.previewBadgeData$.pipe(takeUntil(this.destroyed$)).subscribe((previewBadgeData) => {
        this.badgeQuestions = previewBadgeData;
        this.updateFixedValues();
      });
      return;
    }
    this.clearFixedVariables();

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.visual_indicators)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((newVisualIndicators) => (this.newVisualIndicators = newVisualIndicators));

    const observable = combineLatest({
      badgeUserId: this.badgingService.badgeUserIdOpen$,
      badgeData: this.badgingService.badgeData$,
      badgeUserRingColor: this.badgingService.badgeUserRingColor$,
    });

    observable
      .pipe(
        distinctUntilChanged((a, b) => _.isEqual(a, b)),
        takeUntil(this.destroyed$),
      )
      .subscribe((value) => {
        // TODO: this is firing with bad data when a user is removed from or added to the scene, the following check should not be necessary
        if (!value.badgeUserId || value.badgeData.length === 0) {
          return;
        }
        // return if data is the same
        if (this.userId === value.badgeUserId && _.isEqual(this.badgeQuestions, value.badgeData)) {
          return;
        }
        this.resetDragPosition();
        // Handle if user tries to open a badge with other badge already opened
        if (this.userId !== value.badgeUserId) {
          this.badgeCardWasOpen = false;
        }
        this.userId = value.badgeUserId;
        this.userOpenOwnBadge = this.userId === this.mainPlayerId;
        this.badgeQuestions = value.badgeData;
        this.badgeQuestions.forEach((question) => {
          if (question.multiValues) {
            question.value = question.multiValues.join(', ');
          }
        });
        void this.handleActionButtons();
        this.updateFixedValues();
        this.ringColor = this.sceneService.getPlayer(this.userId)?.ringColor ?? value.badgeUserRingColor;
      });

    // modal should only be thrown when the value changes
    this.favoriteService.favoriteResultStream$.pipe(skip(1), takeUntil(this.destroyed$)).subscribe((resp) => {
      if (resp.userId && resp.userId !== '') {
        this.treatFavoriteResponse(resp);
      }
    });
  }

  // Handle action buttons
  private async handleActionButtons(): Promise<void> {
    // Check if user is opening his own badge and hide the action icons
    this.user = await this.getUser(this.userId as string);
    if (this.userId === this.mainPlayerId) {
      this.showRoomActionIcons = false;
    } else {
      // If user is opening another user badge, the actions icons should showed
      this.showRoomActionIcons = true;

      // Check if user is presenting to a room
      if (!this.isUserPresentingToGroup) {
        this.store
          .select(socialAppSelectors.isUserPresenting)
          .pipe(takeUntil(this.destroyed$))
          .subscribe((isUserPresenting) => {
            if (isUserPresenting) {
              this.isUserPresentingToRoom = isUserPresenting;
              this.isOnPresentation = true;
              this.blockActionMessage = `This feature is unavailable during a presentation`;
            }
          });
      }

      // Check if user is presenting to a group
      if (!this.isUserPresentingToRoom) {
        this.store
          .select(socialAppSelectors.isUserSharingScreenToGroup)
          .pipe(takeUntil(this.destroyed$))
          .subscribe((isUserSharing) => {
            if (isUserSharing) {
              this.isUserPresentingToGroup = isUserSharing;
              this.isOnPresentation = true;
              this.blockActionMessage = `This feature is unavailable during a presentation`;
            }
          });
      }

      // Check if the user is in the gallery but is not the presenter
      if (!this.isUserPresentingToRoom && !this.isUserPresentingToGroup) {
        this.store
          .select(socialAppSelectors.getGallery)
          .pipe(takeUntil(this.destroyed$))
          .subscribe((gallery) => {
            const isUserInGallery = gallery
              .map((g) => g.userId)
              .filter(notNil)
              .filter((id) => this.mainPlayerId === id);

            if (isUserInGallery.length > 0) {
              this.isOnPresentation = true;
              this.blockActionMessage = `This feature is unavailable during a presentation`;
            }
          });
      }

      // If the badge is on viewport only the chat action will be visible
      if (this.badgingService.isSelectedInViewport) {
        this.isOnViewport = true;
        this.blockActionMessage = `You are too close to teleport`;
      }

      if (this.social?.userId) {
        this.mainPlayer = this.sceneService.getPlayer(this.mainPlayerId);
      }

      if (this.userId) {
        try {
          this.baseUser = (await this.getUser(this.userId, this.social?.socialId)) as BaseUser;
          if (this.social?.socialId !== this.baseUser.socialId) {
            this.blockActionButtons = true;
            this.store
              .select(socialAppSelectors.getCurrentConnectedRoomSet)
              .pipe(filter(notNil), takeUntil(this.destroyed$))
              .subscribe((set) => {
                this.connectedRoomSet = set.socials ?? [];

                const connectedRoomInfo = this.connectedRoomSet.find((room) => {
                  return room.socialId === this.baseUser?.socialId;
                });

                this.roomName = connectedRoomInfo?.name;
              });

            this.blockActionMessage = `${this.formatName(
              this.baseUser.firstName,
              this.baseUser.lastName,
            )} is in ${this.checkRoomName()}. Change room on map dropdown to navigate to that room.`;

            return;
          }

          if (this.userId !== this.mainPlayerId) {
            this.player = this.sceneService.getPlayer(this.userId);
          }
        } catch (e) {
          this.error('Failed to get user info', e);
        }
      }
    }
  }

  // Get question name from array
  private getQuestionProperty(property: string): BadgeDataValue | undefined {
    return this.badgeQuestions?.find((question) => question.propertyName === property);
  }

  /** FIXED BADGE VALUES - Scoot Profile*/
  private updateFixedValues(): void {
    this.userName = this.updateUserName()?.trim() as string;
    this.badgeSubtitle = this.updateBadgeSubtitle();
    this.badgeLinkedIn = this.updateBadgeLinkedIn();
    this.badgeAboutMe = this.updateBadgeAboutMe();
  }

  private updateUserName(): string | undefined {
    // Handle preview badge scenario
    if (this.isPreviewCard) {
      return this.propertyHasValue('First Name') && this.propertyHasValue('Last Name')
        ? this.formatName(this.getQuestionProperty('First Name')?.value, this.getQuestionProperty('Last Name')?.value)
        : this.previewBadgeInfo?.userName;
    } else if (this.isSummaryView || this.summaryTimeline) {
      return this.propertyHasValue('First Name') && this.propertyHasValue('Last Name')
        ? this.formatName(this.user?.firstName, this.user?.lastName)
        : (this.previewBadgeInfo?.userName ?? '');
    }

    return this.propertyHasValue('First Name') && this.propertyHasValue('Last Name')
      ? this.formatName(this.getQuestionProperty('First Name')?.value, this.getQuestionProperty('Last Name')?.value)
      : '';
  }

  private updateBadgeSubtitle(): string {
    const badgeTitle = this.getBadgeTitle();
    const badgeCompany = this.getCompanyName();

    if (badgeTitle && badgeCompany) {
      return `${badgeTitle} @ ${badgeCompany}`;
    }

    if (badgeTitle) {
      return `${badgeTitle}`;
    }

    return badgeCompany ? `${badgeCompany}` : '';
  }

  private getBadgeTitle(): string | undefined {
    // Handle preview badge scenario
    if (this.isPreviewCard || this.isSummaryView) {
      return this.getQuestionProperty('Title')?.value ?? this.previewBadgeInfo?.userTitle;
    }
    return this.getQuestionProperty('Title')?.value;
  }

  private getCompanyName(): string | undefined {
    // Handle preview badge scenario
    if (this.isPreviewCard || this.isSummaryView) {
      return this.getQuestionProperty('Company')?.value ?? this.previewBadgeInfo?.companyName;
    }
    return this.getQuestionProperty('Company')?.value;
  }

  private updateBadgeLinkedIn(): string | undefined {
    return this.getQuestionProperty('LinkedIn')?.value ?? this.previewBadgeInfo?.linkedIn;
  }

  private updateBadgeAboutMe(): string | undefined {
    if (this.isPreviewCard) {
      return this.getQuestionProperty('About Me')?.value ?? this.previewBadgeInfo?.aboutMe;
    }

    return this.getQuestionProperty('About Me')?.value;
  }

  /** DYNAMIC BADGE VALUES */
  // Return only featured questions
  public get featuredQuestions(): BadgeDataValue[] {
    if (this.badgeQuestions) {
      return this.badgeQuestions.filter(
        (question) => MAIN_QUESTIONS.indexOf(question.propertyName) === -1 && question.featured,
      );
    } else {
      return [];
    }
  }

  // Return only questions that aren't featured
  public get tableQuestions(): BadgeDataValue[] {
    if (this.badgeQuestions) {
      return this.badgeQuestions.filter(
        (question) => MAIN_QUESTIONS.indexOf(question.propertyName) === -1 && !question.featured,
      );
    } else {
      return [];
    }
  }

  public get shouldShowDetailsSection(): boolean {
    return (this.shouldShowTableRow && this.badgeIsEnabled) || this.isHost;
  }

  public get disableDrag(): boolean | undefined {
    return this.isOnboardingExperience() ? false : this.isPreviewCard || this.customStyle || this.summaryTimeline;
  }

  public get shouldShowFeaturedRow(): boolean {
    return (
      this.featuredQuestions.length > 0 && this.featuredQuestions.some((question) => this.shouldShowQuestion(question))
    );
  }

  public get shouldShowTableRow(): boolean {
    return this.tableQuestions.length > 0 && this.tableQuestions.some((question) => this.shouldShowQuestion(question));
  }

  public shouldShowQuestion(question: BadgeDataValue): boolean {
    return question && question.value !== undefined && question.value !== '';
  }

  //** Utils icons functions */
  // Open and Close Chat menu
  public async toggleChat(): Promise<void> {
    this.chatEngaged = true;
    this.chatService.toggleTriggerPrivateMessageBadge(this.userId as string);
    await delayFor(1500);
    this.chatEngaged = false;
  }

  public onClickSocial(buttonSelected: string, url: string): void {
    if (url && url !== '') {
      this.goToLink(url);
    }
  }

  public goToLink(url: string) {
    let urlToOpen = '';
    if (!/^http[s]?:\/\//.test(url)) {
      urlToOpen += 'http://';
    }
    urlToOpen += url;
    window.open(urlToOpen, '_blank');
  }

  public openSmartBadgingArticle(): void {
    window.open('https://help.scoot.app/smart-badging', '_blank');
  }

  public closeBadge(): void {
    if (this.isSummaryView || this.summaryTimeline) {
      this.summaryBadgeClosed.emit(true);
    } else {
      this.store.dispatch(socialAppActions.TOGGLE_BADGE_OPEN({ open: false, userId: '', ringColor: '' }));
    }
  }

  public ngOnDestroy(): void {
    this.userId = '';
    this.clearFixedVariables();
    this.badgeQuestions = [];
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private async getUser(userId: string, socialId?: string): Promise<User> {
    return await firstValueFrom(this.qaService.getUser(userId, socialId));
  }

  private formatName(firstName?: string, lastName?: string): string {
    return lastName ? `${firstName} ${lastName}` : `${firstName}`;
  }

  // TODO: refactor into teleport service
  public async teleportToUser(): Promise<void> {
    // Handle a verification of clicking on the button to not close the badging
    await delayFor(100);
    this.teleportEngaged = true;

    await delayFor(1500);
    this.mainPlayer?.fadeOut(1500, 0);
    this.closeBadge();

    await delayFor(1500);
    this.store.dispatch(socialAppActions.TOGGLE_SEARCH_PARTICIPANTS({ open: false }));

    if (this.player) {
      try {
        this.mainPlayer?.fadeIn(1500);
        this.teleportService.onTeleport(this.player?.x.toString(), (this.player?.y + 400).toString());
        this.teleportEngaged = false;
      } catch (e) {
        this.mainPlayer?.fadeIn(1500);
        this.store.dispatch(
          socialAppActions.TOGGLE_BADGE_OPEN({ open: true, userId: `${this.userId}`, ringColor: this.ringColor }),
        );
        this.teleportEngaged = false;
        this.error('Failed to teleport user to other participant', e);
      }
    } else {
      this.error('Failed to teleport user to other participant, player not found');
    }
  }

  public async findUserOnMap(): Promise<void> {
    this.findEngaged = true;
    this.store.dispatch(socialAppActions.TOGGLE_SEARCH_PARTICIPANTS({ open: false }));
    this.store.dispatch(socialAppActions.TOGGLE_MAP_OPEN({ open: true, userId: this.userId as string }));
    await delayFor(1500);
    this.findEngaged = false;
  }

  private clearFixedVariables(): void {
    this.userName = '';
    this.badgeSubtitle = '';
    this.badgeLinkedIn = '';
    this.badgeAboutMe = '';
  }

  // Reset position of badge after being dragged
  private resetDragPosition(): void {
    this.dragPosition = { x: this.dragPosition.x, y: this.dragPosition.y };
  }

  protected get aboutMeShowButton() {
    if (!this.aboutMeSection || !this.aboutMeSection.nativeElement) {
      return false;
    }

    return this.aboutMeSection?.nativeElement?.clientHeight > MAX_ABOUT_ME_HEIGHT;
  }

  public featureChipMaxWidth(text: string): boolean {
    return text?.length >= this.featureChipMaxLength;
  }

  public get badgeIsEnabled(): boolean {
    return this.badgingService.smartBadgingEnabled;
  }

  public handleShowMoreLessBtn(): void {
    if (this.aboutMeSection) {
      this.aboutMeSection.nativeElement.scrollTop = 0;
    }
    this.showBtn = !this.showBtn;
  }

  private checkRoomName(): string {
    return this.roomName?.toUpperCase().endsWith('ROOM') ? `${this.roomName}` : `${this.roomName} Room`;
  }

  public shouldShowIndicator(value: string): boolean {
    return value === this.visualIndicatorQuestion;
  }

  private openFavoriteNotification(message: string, type?: SnackBarType, icon?: string, duration = 2000): void {
    const _data: SnackBarData = {
      message: message,
      type: type || '',
      icon: icon || '',
      mode: this.presentationService.isPresentationActive ? 'dark' : 'light',
    };
    this._snackBar.openFromComponent(SnackBarComponent, {
      duration: duration,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'custom-snackbar',
      data: _data,
    });
  }

  private treatFavoriteResponse(favoriteResponse: FavoriteResponse): void {
    if (favoriteResponse.userId && favoriteResponse.userId !== '') {
      if (favoriteResponse.error) {
        this.openFavoriteNotification('Error: ' + favoriteResponse.error, 'error');
      } else {
        if (favoriteResponse.add) {
          this.openFavoriteNotification(
            `${this.userName} has been added to your favorites`,
            'success',
            'FAVORITE_STAR_FILL_SUCCESS',
          );
        } else {
          this.openFavoriteNotification(
            `${this.userName} has been removed from your favorites`,
            'success',
            'FAVORITE_STAR_FILL_SUCCESS',
          );
        }
      }
    }
  }

  protected openEditBadge() {
    this.store.dispatch(socialAppActions.UPDATE_BADGING_MODAL({ open: true }));
    this.closeBadge();
  }

  protected get isOwnBadge() {
    return this.mainPlayerId === this.userId;
  }

  public get isFavorite(): boolean {
    if (this.userId) {
      return this.favoriteService.getFavoriteList().includes(this.userId);
    }
    return false;
  }

  public get roomHasQuestions() {
    return this.shouldShowTableRow || this.shouldShowFeaturedRow;
  }

  public get showEditIcon() {
    return !this.isPreviewCard && !this.isSummaryView && this.isOwnBadge && this.roomHasQuestions;
  }

  public clickedFavoriteIcon(): void {
    if (this.isFavorite) {
      this.favoriteService.unfavoriteParticipant(this.mainPlayerId, this.userId);
    } else {
      this.favoriteService.favoriteParticipant(this.mainPlayerId, this.userId);
    }
  }

  private propertyHasValue(control: string): boolean {
    return !!this.getQuestionProperty(control)?.value;
  }
}
